import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, withModifiers as _withModifiers, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, renderSlot as _renderSlot, createTextVNode as _createTextVNode, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "c-co-table" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = ["name", "checked"]
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "table__head" }
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = { class: "row--group-header" }
const _hoisted_8 = ["colspan"]
const _hoisted_9 = { class: "group-header-inner" }
const _hoisted_10 = { class: "group-badge" }
const _hoisted_11 = {
  key: 0,
  class: "group-time"
}
const _hoisted_12 = {
  key: 1,
  class: "group-count"
}
const _hoisted_13 = ["onClick"]
const _hoisted_14 = ["onClick"]
const _hoisted_15 = {
  key: 0,
  class: "row--new-task"
}
const _hoisted_16 = ["onClick", "onDblclick"]
const _hoisted_17 = {
  key: 1,
  class: "row--empty-group"
}
const _hoisted_18 = ["colspan"]
const _hoisted_19 = {
  key: 0,
  class: "row--new-task"
}
const _hoisted_20 = ["id", "onClick", "onDblclick"]
const _hoisted_21 = ["value", "name", "onClick", "onChange"]
const _hoisted_22 = { key: 2 }
const _hoisted_23 = ["colspan"]
const _hoisted_24 = { class: "no__data--body" }

import { Inbox01, ChevronDown, Eye, EyeOff, Check } from "utruckin-icons";
import { ITableHeader, ITableProps } from "@/types/table";
import CustomLoading from "@/components/custom/CustomLoading.vue";
import { computed, nextTick, onBeforeMount, onMounted, ref } from "vue";
import { isEqual } from "lodash-es";

interface ITableGroup {
  label: string;
  sublabel?: string;
  count?: number;
  items: any[];
}

interface ITablePropsExtended extends ITableProps {
  groups?: ITableGroup[];
  visibility?: boolean;
  isTaskTable?: boolean;
  selectMode: boolean;
  dateIndex?: number;
  shiftIndex?: number;
  isAdmin?: boolean;
  withoutHeader?: boolean;
  withoutShift?: boolean;
  stickyColumns?: number;
}

const SELECT_COL_WIDTH = 48;


export default /*@__PURE__*/_defineComponent({
  __name: 'CommonTable',
  props: {
    groups: { default: undefined },
    visibility: { type: Boolean, default: true },
    isTaskTable: { type: Boolean, default: false },
    selectMode: { type: Boolean, default: false },
    dateIndex: {},
    shiftIndex: {},
    isAdmin: { type: Boolean, default: false },
    withoutHeader: { type: Boolean, default: false },
    withoutShift: { type: Boolean, default: false },
    stickyColumns: { default: 0 },
    loading: { type: Boolean, default: false },
    headers: { default: undefined },
    items: { default: undefined },
    striped: { type: Boolean, default: true },
    type: {}
  },
  emits: ["update:selected", "select", "deselect", "row-click", "row-db-click"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emits = __emit;

const uid = ref("");
const collapsedGroups = ref<Set<number>>(new Set());
const selectedRecords = ref<any[]>([]);
const lastSelectedIdx = ref<number>();
const selectHeader = {
  name: "id",
  label: "No",
  fitContent: true,
  type: "centert",
};
const scrollToRow = async (id: number | string) => {
  await nextTick();

  const element = document.getElementById(`row-${id}`);

  if (element) {
    element.scrollIntoView({
      block: "center",
    });
  }
};

__expose({
  scrollToRow,
});

const parsePx = (value?: string) => {
  if (!value) return 120; // fallback if a sticky column has no explicit width
  const num = parseFloat(value);
  return Number.isNaN(num) ? 120 : num;
};

const isSticky = (index: number) =>
  !!props.stickyColumns && index < props.stickyColumns;

const getStickyLeft = (index: number) => {
  let left = props.selectMode ? SELECT_COL_WIDTH : 0;
  for (let i = 0; i < index; i++) {
    left += parsePx(props.headers?.[i]?.width);
  }
  return `${left}px`;
};

const getColumnStyle = (
  header: ITableHeader,
  index?: number,
  cellType: "th" | "td" = "td"
) => {
  const style: Record<string, string> = {};

  if (header.fitContent) {
    style.width = "5%";
    style.whiteSpace = "nowrap";
  } else if (header.width) {
    style.width = header.width;
  } else {
    style.width = "auto";
  }

  if (index !== undefined && isSticky(index)) {
    style.position = "sticky";
    style.left = getStickyLeft(index);
    style.zIndex = cellType === "th" ? "3" : "1";
    if (cellType === "td") style.background = "inherit";
  }

  return style;
};

const selectColStyle = computed(() =>
  props.selectMode && props.stickyColumns
    ? { position: "sticky", left: "0px", zIndex: "3" }
    : {}
);

const selectColBodyStyle = computed(() =>
  props.selectMode && props.stickyColumns
    ? { position: "sticky", left: "0px", zIndex: "1", background: "inherit" }
    : {}
);

const selectedModel = computed({
  get() {
    return selectedRecords.value;
  },
  set(value) {
    emits("update:selected", value);
    selectedRecords.value = value;
  },
});

const isAllSelected = computed(() => {
  if (props.items?.length === selectedModel.value?.length) {
    return props.items?.every((row, idx) =>
      isEqual(row, selectedModel.value?.at(idx))
    );
  }

  return false;
});

function toggleGroup(idx: number) {
  if (collapsedGroups.value.has(idx)) {
    collapsedGroups.value.delete(idx);
  } else {
    collapsedGroups.value.add(idx);
  }
  collapsedGroups.value = new Set(collapsedGroups.value);
}

const isCollapsed = (idx: number) => collapsedGroups.value.has(idx);

const selectAll = (event: Event) => {
  const checked = (event.target as HTMLInputElement).checked;

  if (checked) {
    selectedModel.value = [...(props.items || [])];
  } else {
    selectedModel.value = [];
  }
};

const rangeSelect = (row: any, idx: number) => {
  if (lastSelectedIdx.value !== -1) {
    const start = Math.min(lastSelectedIdx.value!, idx) + 1;
    const end = Math.max(lastSelectedIdx.value!, idx);
    for (let i = start; i <= end; i++) {
      const value = props.items[i];
      selectedModel.value?.push(value);
    }
  }
};

const onChange = (row: any, rowIdx: number, event: Event) => {
  const checked = (event.target as HTMLInputElement).checked;
  if (checked) {
    lastSelectedIdx.value = rowIdx;
    emits("select", row);
  } else emits("deselect", row);
};

const isSelected = (item: any) => {
  return selectedModel.value.some((row: any) => row.id === item.id);
};

const rowClick = (item: any, index: number) => {
  emits("row-click", item, index);
};

const rowDbClick = (item: any, index: number) => {
  emits("row-db-click", item, index);
};

onBeforeMount(() => {
  uid.value = crypto.randomUUID();
});

onMounted(async () => {
  await nextTick();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("table", {
      class: _normalizeClass(["table", { striped: __props.striped }])
    }, [
      _createElementVNode("colgroup", null, [
        (__props.selectMode)
          ? (_openBlock(), _createElementBlock("col", {
              key: 0,
              style: _normalizeStyle(getColumnStyle(selectHeader))
            }, null, 4))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.headers, (header, index) => {
          return (_openBlock(), _createElementBlock("col", {
            key: index,
            style: _normalizeStyle(getColumnStyle(header))
          }, null, 4))
        }), 128))
      ]),
      (!__props.withoutHeader)
        ? (_openBlock(), _createElementBlock("thead", _hoisted_2, [
            _createElementVNode("tr", null, [
              (__props.selectMode)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    (__props.items?.length)
                      ? (_openBlock(), _createElementBlock("th", {
                          key: 0,
                          class: "ui-table-select-col",
                          style: _normalizeStyle(selectColStyle.value)
                        }, [
                          _createElementVNode("label", {
                            class: _normalizeClass(["custom__checkbox", isAllSelected.value ? 'active' : ''])
                          }, [
                            _createElementVNode("input", {
                              class: "ui-checkbox form-checkbox",
                              type: "checkbox",
                              name: uid.value,
                              checked: isAllSelected.value,
                              onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
                              onChange: selectAll
                            }, null, 40, _hoisted_3),
                            _createVNode(_unref(Check), { size: 14 })
                          ], 2)
                        ], 4))
                      : (_openBlock(), _createElementBlock("th", _hoisted_4))
                  ], 64))
                : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.headers, (item, index) => {
                return (_openBlock(), _createElementBlock("th", {
                  key: index,
                  style: _normalizeStyle(getColumnStyle(item, index, 'th')),
                  class: _normalizeClass({
              'sticky-col-last': isSticky(index) && index === __props.stickyColumns - 1,
            })
                }, [
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("span", {
                      class: "table__head--label",
                      innerHTML: item.label
                    }, null, 8, _hoisted_6)
                  ])
                ], 6))
              }), 128))
            ])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("tbody", null, [
        (__props.groups && __props.groups.length)
          ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(__props.groups, (group, groupIdx) => {
              return (_openBlock(), _createElementBlock(_Fragment, { key: groupIdx }, [
                _createElementVNode("tr", _hoisted_7, [
                  _createElementVNode("td", {
                    colspan: __props.headers?.length
                  }, [
                    _createElementVNode("div", _hoisted_9, [
                      _createElementVNode("span", _hoisted_10, _toDisplayString(group.label), 1),
                      (group.sublabel)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(group.sublabel), 1))
                        : _createCommentVNode("", true),
                      (group.count !== undefined)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(group.count) + " Users ", 1))
                        : _createCommentVNode("", true),
                      (isCollapsed(groupIdx))
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 2,
                            class: "group-toggle",
                            onClick: _withModifiers(($event: any) => (toggleGroup(groupIdx)), ["stop"])
                          }, [
                            _createVNode(_unref(Eye), { size: 20 })
                          ], 8, _hoisted_13))
                        : (_openBlock(), _createElementBlock("button", {
                            key: 3,
                            class: "group-toggle",
                            onClick: _withModifiers(($event: any) => (toggleGroup(groupIdx)), ["stop"])
                          }, [
                            _createVNode(_unref(EyeOff), { size: 20 })
                          ], 8, _hoisted_14))
                    ])
                  ], 8, _hoisted_8)
                ]),
                (!isCollapsed(groupIdx))
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      (!collapsedGroups.value.has(groupIdx))
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            (
                    __props.dateIndex === 0 &&
                    __props.shiftIndex == 0 &&
                    __props.isTaskTable &&
                    !__props.isAdmin
                  )
                              ? (_openBlock(), _createElementBlock("tr", _hoisted_15, [
                                  _renderSlot(_ctx.$slots, "new-row")
                                ]))
                              : _createCommentVNode("", true),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.items, (item, rowIdx) => {
                              return (_openBlock(), _createElementBlock("tr", {
                                key: `${groupIdx}-${rowIdx}`,
                                class: _normalizeClass({
                    'row--inactive': item.is_active === false,
                    [item.status]: !!item.status,
                  }),
                                onClick: ($event: any) => (rowClick(item, rowIdx)),
                                onDblclick: ($event: any) => (rowDbClick(item, rowIdx))
                              }, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.headers, (cellItem, cellIdx) => {
                                  return (_openBlock(), _createElementBlock("td", {
                                    key: cellIdx,
                                    class: _normalizeClass([
                      cellItem.type == 'error' ? 'error' : '',
                      cellItem.type == 'day' ? 'day-cell-td' : '',
                      isSticky(cellIdx) && cellIdx === __props.stickyColumns - 1
                        ? 'sticky-col-last'
                        : '',
                    ]),
                                    style: _normalizeStyle(getColumnStyle(cellItem, cellIdx, 'td'))
                                  }, [
                                    _renderSlot(_ctx.$slots, cellItem.name, {
                                      item: item,
                                      index: rowIdx
                                    }, () => [
                                      _createTextVNode(_toDisplayString(item[cellItem.name]), 1)
                                    ])
                                  ], 6))
                                }), 128))
                              ], 42, _hoisted_16))
                            }), 128))
                          ], 64))
                        : _createCommentVNode("", true),
                      (!group.items.length && !collapsedGroups.value.has(groupIdx))
                        ? (_openBlock(), _createElementBlock("tr", _hoisted_17, [
                            _createElementVNode("td", {
                              colspan: __props.headers?.length
                            }, [...(_cache[2] || (_cache[2] = [
                              _createElementVNode("span", { class: "group-empty" }, "No users in this shift", -1)
                            ]))], 8, _hoisted_18)
                          ]))
                        : _createCommentVNode("", true)
                    ], 64))
                  : _createCommentVNode("", true)
              ], 64))
            }), 128))
          : (__props.items && __props.items.length)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                (__props.dateIndex === 0 && __props.isTaskTable && !__props.isAdmin)
                  ? (_openBlock(), _createElementBlock("tr", _hoisted_19, [
                      _renderSlot(_ctx.$slots, "new-row")
                    ]))
                  : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.items, (item, rowIdx) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: rowIdx,
                    id: `row-${item.id}`,
                    class: _normalizeClass({
              'row--inactive': item.is_active === false,
              [item.status]: !!item.status,
            }),
                    onClick: ($event: any) => (rowClick(item, rowIdx)),
                    onDblclick: ($event: any) => (rowDbClick(item, rowIdx))
                  }, [
                    (__props.selectMode)
                      ? (_openBlock(), _createElementBlock("td", {
                          key: 0,
                          style: _normalizeStyle(selectColBodyStyle.value)
                        }, [
                          _createElementVNode("label", {
                            class: _normalizeClass(["custom__checkbox", isSelected(item) ? 'active' : ''])
                          }, [
                            _withDirectives(_createElementVNode("input", {
                              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedModel).value = $event)),
                              value: item,
                              class: "ui-checkbox form-checkbox",
                              type: "checkbox",
                              name: uid.value,
                              onClick: _withModifiers(($event: any) => (rangeSelect(item, rowIdx)), ["shift","exact"]),
                              onChange: ($event: any) => (onChange(item, rowIdx, $event))
                            }, null, 40, _hoisted_21), [
                              [_vModelCheckbox, selectedModel.value]
                            ]),
                            _createVNode(_unref(Check), { size: 14 })
                          ], 2)
                        ], 4))
                      : _createCommentVNode("", true),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.headers, (cellItem, cellIdx) => {
                      return (_openBlock(), _createElementBlock("td", {
                        key: cellIdx,
                        class: _normalizeClass([
                cellItem.type == 'error' ? 'error' : '',
                cellItem.type == 'day' ? 'day-cell-td' : '',
                isSticky(cellIdx) && cellIdx === __props.stickyColumns - 1
                  ? 'sticky-col-last'
                  : '',
              ]),
                        style: _normalizeStyle(getColumnStyle(cellItem, cellIdx, 'td'))
                      }, [
                        _renderSlot(_ctx.$slots, cellItem.name, {
                          item: item,
                          index: rowIdx
                        }, () => [
                          _createTextVNode(_toDisplayString(item[cellItem.name]), 1)
                        ])
                      ], 6))
                    }), 128))
                  ], 42, _hoisted_20))
                }), 128))
              ], 64))
            : (_openBlock(), _createElementBlock("tr", _hoisted_22, [
                _createElementVNode("td", {
                  class: "no__data",
                  colspan: __props.selectMode ? __props.headers?.length + 1 : __props.headers?.length
                }, [
                  _createElementVNode("div", _hoisted_24, [
                    _createVNode(_unref(Inbox01), { size: 40 }),
                    _cache[3] || (_cache[3] = _createTextVNode(" Data not found ", -1))
                  ])
                ], 8, _hoisted_23)
              ]))
      ])
    ], 2)
  ]))
}
}

})